import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAPI } from "../../providers/ApiProvider";

/**
 * Data + editor plumbing for the export preview page.
 *
 * - Loads a stable, seeded sample of tasks through the export's own
 *   serializer (shaped with the chosen annotation rule + field list).
 * - Mounts the read-only editor for the current task into `rootRef`.
 * - Tracks the region selected in the editor so the record pane can
 *   highlight the matching result, and vice versa.
 */

let editorDeps;
const loadEditor = async () => {
  if (!editorDeps) editorDeps = import("@humansignal/editor");
  return editorDeps;
};

export const PAGE = 20;

export const useExportSample = ({
  projectId,
  viewId,
  downloadAllTasks = false,
  annotationChoice = "all",
  fields = null,
}) => {
  const api = useAPI();
  const [page, setPage] = useState(null);
  const [seed, setSeed] = useState(null);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedResultId, setSelectedResultId] = useState(null);
  const rootRef = useRef(null);
  const lsf = useRef(null);

  const load = useCallback(
    async (offset = 0, newSeed = seed, keepIndex = false) => {
      setLoading(true);
      const params = {
        pk: projectId,
        download_all_tasks: downloadAllTasks,
        limit: PAGE,
        offset,
        annotation_choice: annotationChoice,
      };
      if (newSeed != null) params.seed = newSeed;
      if (viewId) params.view = viewId;
      if (fields && fields.length) params.fields = fields.join(",");
      const res = await api.callApi("exportPreview", { params, errorFilter: () => true });
      if (res?.tasks) {
        setPage(res);
        setSeed(res.seed);
        if (!keepIndex) setIndex(0);
      }
      setLoading(false);
    },
    [api, projectId, downloadAllTasks, seed, viewId, annotationChoice, fields],
  );

  useEffect(() => {
    load(page?.offset ?? 0, seed, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, downloadAllTasks, viewId, annotationChoice, fields]);

  const rawTask = page?.raw_tasks?.[index] ?? null;
  const shapedTask = page?.tasks?.[index] ?? null;
  const annotation = shapedTask?.annotations?.[0] ?? null;
  const results = useMemo(() => annotation?.result ?? [], [annotation]);
  const inventory = page?.fields_available ?? { task: [], data: [], annotation: [], result: [] };

  // Mount / rebuild the read-only editor for the current task.
  useEffect(() => {
    let cancelled = false;
    const mount = async () => {
      await loadEditor();
      if (cancelled || !rawTask || !rootRef.current) return;
      if (lsf.current) {
        try {
          lsf.current.destroy();
        } catch (_e) {
          /* ignore */
        }
        lsf.current = null;
      }
      rootRef.current.innerHTML = "";
      try {
        lsf.current = new window.LabelStudio(rootRef.current, {
          config: page.label_config,
          task: {
            id: rawTask.id,
            data: rawTask.data,
            annotations: shapedTask?.annotations?.length ? shapedTask.annotations : (rawTask.annotations ?? []),
            predictions: [],
          },
          interfaces: [],
          instanceOptions: { reactVersion: "v18" },
          onStorageInitialized(LS) {
            setTimeout(() => {
              const as = LS.annotationStore;
              const first = as.annotations?.[0];
              if (first) {
                as.selectAnnotation(first.id);
                first.setReadonly?.(true);
              }
            });
          },
        });
      } catch (err) {
        console.error(err);
      }
    };
    mount();
    return () => {
      cancelled = true;
    };
  }, [rawTask, shapedTask, page?.label_config]);

  // Follow the editor's selection for region ↔ record linking.
  useEffect(() => {
    const t = window.setInterval(() => {
      const node = lsf.current?.store?.annotationStore?.selected?.highlightedNode;
      const id = node?.cleanId ?? node?.id?.split?.("#")?.[0] ?? null;
      setSelectedResultId((cur) => (cur === id ? cur : id));
    }, 250);
    return () => window.clearInterval(t);
  }, []);

  useEffect(
    () => () => {
      try {
        lsf.current?.destroy();
      } catch (_e) {
        /* ignore */
      }
    },
    [],
  );

  const selectRegion = useCallback((resultId) => {
    const as = lsf.current?.store?.annotationStore?.selected;
    if (!as) return;
    const region = as.regionStore?.regions?.find?.((r) => (r.cleanId ?? r.id) === resultId);
    if (region) as.selectArea?.(region);
  }, []);

  const total = page?.count ?? 0;
  const skipped = page?.skipped ?? 0;
  const pos = page ? page.offset + index + 1 : 0;
  const prev = () => (index > 0 ? setIndex(index - 1) : load(Math.max(0, page.offset - PAGE)));
  const next = () => (index < (page?.raw_tasks?.length ?? 0) - 1 ? setIndex(index + 1) : load(page.offset + PAGE));
  const shuffle = () => load(0, Math.floor(Math.random() * 1e6));

  return {
    loading,
    total,
    skipped,
    pos,
    prev,
    next,
    shuffle,
    rawTask,
    shapedTask,
    annotation,
    results,
    inventory,
    rootRef,
    selectedResultId,
    selectRegion,
  };
};
